/**
 * AI settings (ARCHITECTURE.md §8, §6, §10).
 *
 * One card per built-in provider (OpenAI, Anthropic), a card for a generic
 * "custom" OpenAI-compatible endpoint (OpenRouter / Ollama / LM Studio / vLLM),
 * and a "model aliases" card mapping each internal role to a registry ref. Each
 * credential card adds/replaces a secret then **tests** it — a successful probe
 * unlocks the `ai` gate. Secrets are write-only: the API returns only the masked
 * tail and never the plaintext.
 */
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useI18n } from "@richrich/i18n/react";
import {
  dropNullish,
  type AiModelProvider,
  type CredentialAuthType,
  type CredentialSummary,
  type ModelAlias,
  type PutAiAliases,
  type PutCredential,
} from "@richrich/contracts";
import type { MessageKey } from "@richrich/i18n";
import { PageShell } from "../../components/PageShell";
import { settingsQuery } from "../../lib/query";
import { aiAliasesQuery, aiModelsQuery, credentialsQuery } from "../../features/settings/api";
import { usePutAiAliases, usePutCredential } from "../../features/settings/hooks";
import {
  AI_PROVIDERS,
  MODEL_META_KEY,
  type AiProviderCatalog,
} from "../../features/settings/catalog";
import {
  Button,
  ErrorNote,
  SectionCard,
  Select,
  Spinner,
  TextField,
} from "../../components/ui/controls";
import { CredentialActions, CredentialStatus } from "../../features/settings/CredentialControls";

export const Route = createFileRoute("/settings/ai")({
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(settingsQuery),
      context.queryClient.ensureQueryData(credentialsQuery),
      context.queryClient.ensureQueryData(aiAliasesQuery),
      context.queryClient.ensureQueryData(aiModelsQuery),
    ]);
  },
  component: AiSettingsPage,
});

function AiSettingsPage() {
  const { t } = useI18n();
  const credentials = useQuery(credentialsQuery);
  const byKind = new Map(credentials.data?.credentials.map((c) => [c.kind, c]));

  return (
    <PageShell
      eyebrow={t("settings.aiEyebrow")}
      title={t("settings.gateAiTitle")}
      description={t("settings.aiDescription")}
      loading={credentials.isLoading}
    >
      <div className="flex flex-col gap-6">
        {AI_PROVIDERS.map((provider) => (
          <ProviderCard
            key={provider.kind}
            provider={provider}
            summary={byKind.get(provider.kind)}
          />
        ))}
        <CustomProviderCard summary={byKind.get("ai_custom")} />
        <ModelAliasesCard />
      </div>
    </PageShell>
  );
}

function currentModel(summary: CredentialSummary | undefined): string | undefined {
  const value = summary?.meta?.[MODEL_META_KEY];
  return typeof value === "string" ? value : undefined;
}

function ProviderCard({
  provider,
  summary,
}: {
  provider: AiProviderCatalog;
  summary: CredentialSummary | undefined;
}) {
  const { t } = useI18n();
  const put = usePutCredential(provider.kind);
  const supportsOauth = provider.authTypes.includes("oauth_bearer");

  const [authType, setAuthType] = useState<CredentialAuthType>("api_key");
  const [secret, setSecret] = useState("");
  const [betaHeader, setBetaHeader] = useState("");
  const [model, setModel] = useState<string>(
    () => currentModel(summary) ?? provider.models[0]?.id ?? "",
  );

  function submit() {
    if (secret.trim().length === 0) return;
    const meta = model.length > 0 ? { [MODEL_META_KEY]: model } : undefined;
    const body: PutCredential =
      authType === "oauth_bearer"
        ? {
            authType: "oauth_bearer",
            token: secret.trim(),
            ...(betaHeader.trim().length > 0 ? { betaHeader: betaHeader.trim() } : {}),
            ...dropNullish({ meta }),
          }
        : dropNullish({
            authType: "api_key" as const,
            apiKey: secret.trim(),
            meta,
          });
    put.mutate(body, { onSuccess: () => setSecret("") });
  }

  const savedModel = currentModel(summary);

  return (
    <SectionCard
      title={provider.name}
      description={t(provider.keyHintKey)}
      aside={<CredentialStatus summary={summary} />}
      // All provider cards start collapsed — the status pill tells the state at
      // a glance and the page stays scannable (operator request).
      collapsible
      defaultOpen={false}
    >
      <div className="flex flex-col gap-4">
        {savedModel !== undefined ? (
          <p className="text-xs text-muted">
            {t("settings.defaultModelPrefix")}{" "}
            <span className="font-mono text-ink-secondary">{savedModel}</span>
          </p>
        ) : null}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {supportsOauth ? (
            <Select<CredentialAuthType>
              label={t("settings.credentialTypeLabel")}
              value={authType}
              onValueChange={setAuthType}
              options={[
                { value: "api_key", label: t("settings.credentialTypeApiKey") },
                { value: "oauth_bearer", label: t("settings.credentialTypeBearer") },
              ]}
            />
          ) : null}
          <Select
            label={t("settings.defaultModelLabel")}
            value={model}
            onValueChange={setModel}
            options={provider.models.map((m) => ({ value: m.id, label: m.label }))}
          />
        </div>

        <TextField
          label={
            authType === "oauth_bearer" ? t("settings.bearerTokenLabel") : t("settings.apiKeyLabel")
          }
          type="password"
          mono
          autoComplete="off"
          placeholder={provider.keyPlaceholder}
          value={secret}
          onValueChange={setSecret}
          hint={
            summary !== undefined
              ? t("settings.replaceStoredHint")
              : t("settings.storedEncryptedHint")
          }
        />

        {authType === "oauth_bearer" ? (
          <TextField
            label={t("settings.betaHeaderLabel")}
            mono
            placeholder="oauth-2025-04-20"
            value={betaHeader}
            onValueChange={setBetaHeader}
            hint={t("settings.betaHeaderHint")}
          />
        ) : null}

        <div className="flex flex-wrap items-center gap-2 border-t border-hairline pt-4">
          <Button
            variant="primary"
            pending={put.isPending}
            disabled={secret.trim().length === 0}
            onClick={submit}
          >
            {summary !== undefined ? t("settings.replaceKey") : t("settings.saveKey")}
          </Button>
          <CredentialActions kind={provider.kind} summary={summary} />
          {put.isError ? <ErrorNote>{t("settings.saveCredentialFailed")}</ErrorNote> : null}
        </div>
      </div>
    </SectionCard>
  );
}

/** Base URL persisted (non-secret) in the credential summary meta, if any. */
function savedBaseUrl(summary: CredentialSummary | undefined): string | undefined {
  const value = summary?.meta?.["baseUrl"];
  return typeof value === "string" ? value : undefined;
}

/**
 * Generic OpenAI-compatible endpoint (OpenRouter / Ollama Cloud / local Ollama /
 * LM Studio / vLLM). Requires a Base URL plus an API key; keyless local servers
 * accept any placeholder value. Stored under the `ai_custom` credential kind and
 * addressable at runtime as `custom:<modelId>`.
 */
function CustomProviderCard({ summary }: { summary: CredentialSummary | undefined }) {
  const { t } = useI18n();
  const put = usePutCredential("ai_custom");
  const [baseUrl, setBaseUrl] = useState<string>(() => savedBaseUrl(summary) ?? "");
  const [secret, setSecret] = useState("");

  function submit() {
    if (secret.trim().length === 0 || baseUrl.trim().length === 0) return;
    const body: PutCredential = {
      authType: "api_key",
      apiKey: secret.trim(),
      baseUrl: baseUrl.trim(),
    };
    put.mutate(body, { onSuccess: () => setSecret("") });
  }

  const saved = savedBaseUrl(summary);

  return (
    <SectionCard
      title={t("settings.customTitle")}
      description={t("settings.customKeyHint")}
      aside={<CredentialStatus summary={summary} />}
      collapsible
      defaultOpen={false}
    >
      <div className="flex flex-col gap-4">
        {saved !== undefined ? (
          <p className="text-xs text-muted">
            {t("settings.baseUrlLabel")}{" "}
            <span className="font-mono text-ink-secondary">{saved}</span>
          </p>
        ) : null}

        <TextField
          label={t("settings.baseUrlLabel")}
          mono
          autoComplete="off"
          placeholder="https://openrouter.ai/api/v1"
          value={baseUrl}
          onValueChange={setBaseUrl}
          hint={t("settings.baseUrlHint")}
        />

        <TextField
          label={t("settings.apiKeyLabel")}
          type="password"
          mono
          autoComplete="off"
          placeholder="sk-…"
          value={secret}
          onValueChange={setSecret}
          hint={
            summary !== undefined ? t("settings.replaceStoredHint") : t("settings.customApiKeyHint")
          }
        />

        <div className="flex flex-wrap items-center gap-2 border-t border-hairline pt-4">
          <Button
            variant="primary"
            pending={put.isPending}
            disabled={secret.trim().length === 0 || baseUrl.trim().length === 0}
            onClick={submit}
          >
            {summary !== undefined ? t("settings.replaceKey") : t("settings.saveKey")}
          </Button>
          <CredentialActions kind="ai_custom" summary={summary} />
          {put.isError ? <ErrorNote>{t("settings.saveCredentialFailed")}</ErrorNote> : null}
        </div>
      </div>
    </SectionCard>
  );
}

/** The internal roles shown in the alias editor, in display order. */
const ALIAS_ROWS: readonly { alias: ModelAlias; labelKey: MessageKey }[] = [
  { alias: "chat-builder", labelKey: "settings.aliasChatBuilder" },
  { alias: "advisor", labelKey: "settings.aliasAdvisor" },
  { alias: "classify", labelKey: "settings.aliasClassify" },
];

/** The three providers a ref can target, in picker order (mirrors the endpoint). */
const PROVIDER_ORDER = ["openai", "anthropic", "custom"] as const;

/** Split a registry ref `provider:modelId` on the FIRST `:` (model ids contain `/`). */
function splitRef(ref: string): { provider: string; model: string } {
  const i = ref.indexOf(":");
  if (i === -1) return { provider: "", model: ref };
  return { provider: ref.slice(0, i), model: ref.slice(i + 1) };
}

/** Brand names stay English; the generic endpoint is translated. */
function providerLabelBase(provider: string, t: (key: MessageKey) => string): string {
  switch (provider) {
    case "openai":
      return "OpenAI";
    case "anthropic":
      return "Anthropic";
    case "custom":
      return t("settings.aliasProviderCustom");
    default:
      return provider;
  }
}

/**
 * Per-role model picker. Each row derives a `{provider, model}` pair from the
 * effective ref, offers a provider Select (registered providers selectable,
 * unregistered ones greyed) and a `<datalist>`-backed model box autocompleted from
 * that provider's live/static model list (free text still allowed as a fallback).
 * Saving recomposes `${provider}:${model}` and PUTs the full set — the contract is
 * unchanged; the server validates each ref and stores it even at an uncredentialed
 * provider (it fails loud at runtime, §6).
 */
function ModelAliasesCard() {
  const { t } = useI18n();
  const aliases = useQuery(aiAliasesQuery);
  const models = useQuery(aiModelsQuery);
  const data = aliases.data;

  return (
    <SectionCard title={t("settings.aliasesTitle")} description={t("settings.aliasesDescription")}>
      {data !== undefined ? (
        <ModelAliasesForm
          defaults={data.defaults}
          effective={data.aliases}
          providers={models.data?.providers ?? []}
          providersLoaded={models.isSuccess}
          providersLoading={models.isLoading}
        />
      ) : null}
    </SectionCard>
  );
}

function ModelAliasesForm({
  defaults,
  effective,
  providers,
  providersLoaded,
  providersLoading,
}: {
  defaults: Record<ModelAlias, string>;
  effective: Record<ModelAlias, string>;
  providers: readonly AiModelProvider[];
  providersLoaded: boolean;
  providersLoading: boolean;
}) {
  const { t } = useI18n();
  const put = usePutAiAliases();
  const [rows, setRows] = useState<Record<ModelAlias, { provider: string; model: string }>>(() => ({
    "chat-builder": splitRef(effective["chat-builder"]),
    advisor: splitRef(effective.advisor),
    classify: splitRef(effective.classify),
  }));

  const setRow = (alias: ModelAlias, patch: Partial<{ provider: string; model: string }>) =>
    setRows((prev) => ({ ...prev, [alias]: { ...prev[alias], ...patch } }));

  const composed = (alias: ModelAlias): string => {
    const { provider, model } = rows[alias];
    return `${provider}:${model.trim()}`;
  };

  const infoFor = (provider: string): AiModelProvider | undefined =>
    providers.find((p) => p.provider === provider);

  // Provider options are the fixed three; unregistered ones are greyed once the
  // model list has loaded (kept selectable while loading to avoid a flash).
  const providerOptions = PROVIDER_ORDER.map((provider) => {
    const registered = infoFor(provider)?.registered ?? false;
    const suffix = providersLoaded && !registered ? t("settings.aliasProviderUnregistered") : "";
    return {
      value: provider,
      label: `${providerLabelBase(provider, t)}${suffix}`,
      disabled: providersLoaded && !registered,
    };
  });

  function save() {
    const body: PutAiAliases = {
      "chat-builder": composed("chat-builder"),
      advisor: composed("advisor"),
      classify: composed("classify"),
    };
    put.mutate(body);
  }

  return (
    <div className="flex flex-col gap-5">
      {providersLoading ? (
        <p className="flex items-center gap-1.5 text-xs text-muted">
          <Spinner />
          {t("settings.aliasModelsLoading")}
        </p>
      ) : null}

      {ALIAS_ROWS.map(({ alias, labelKey }) => {
        const row = rows[alias];
        const info = infoFor(row.provider);
        const listId = `alias-models-${alias}`;
        const isDefault = composed(alias) === defaults[alias];
        const defaultModel = splitRef(defaults[alias]).model;
        return (
          <div
            key={alias}
            className="flex flex-col gap-2 border-t border-hairline pt-4 first:border-t-0 first:pt-0"
          >
            <span className="text-sm font-medium text-ink">{t(labelKey)}</span>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,13rem)_1fr]">
              <Select
                label={t("settings.aliasProviderLabel")}
                value={row.provider}
                options={providerOptions}
                onValueChange={(value) => setRow(alias, { provider: value })}
              />
              <TextField
                label={t("settings.aliasModelLabel")}
                mono
                autoComplete="off"
                list={listId}
                placeholder={defaultModel}
                value={row.model}
                onValueChange={(value) => setRow(alias, { model: value })}
                hint={
                  isDefault ? `${t("settings.aliasUsingDefault")} ${defaults[alias]}` : undefined
                }
              />
              <datalist id={listId}>
                {(info?.models ?? []).map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </datalist>
            </div>
            {info?.error !== undefined ? (
              <p className="text-xs text-warning">{t("settings.aliasModelsError")}</p>
            ) : null}
          </div>
        );
      })}

      <p className="text-xs text-muted">{t("settings.aliasesHint")}</p>
      <div className="flex flex-wrap items-center gap-2 border-t border-hairline pt-4">
        <Button variant="primary" pending={put.isPending} onClick={save}>
          {t("settings.saveAliases")}
        </Button>
        {put.isError ? <ErrorNote>{t("settings.saveAliasesFailed")}</ErrorNote> : null}
      </div>
    </div>
  );
}
